/**
 * JsonInput — monospace textarea for editing a workflow trigger payload,
 * pre-filled with an example derived from the workflow's JSON Schema.
 *
 * Why not a field-by-field form?
 *   Power users (the primary audience for "Trigger" on a deployed pipeline)
 *   are devs who already know JSON; pasting a known-good payload from
 *   their last run is faster than tabbing through inputs. The schema is
 *   already available — we use it to seed the example so they're not
 *   starting from a blank textarea.
 *
 *   For non-devs, the form-renderer variant lives next door in SchemaForm.js
 *   and can be opted into later via a tab toggle if there's demand.
 *
 * Validation:
 *   The only client-side check is "does it parse as JSON" — the server
 *   is authoritative for schema validation. We report `ok=false` if the
 *   textarea contents don't parse; the parent disables submit.
 *
 * Syntax highlight:
 *   True JSON-token highlighting needs a tokenizer + DOM overlay (or a
 *   library like prism / shiki / monaco). For the trigger modal that's
 *   overkill — we use a monospace font + bracket-matching CSS, which
 *   gets 80% of the readability for 0 deps. If demand grows we swap in
 *   `react-simple-code-editor` + `prismjs` in this same component.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { schemaToExample, prettyJson } from './schema-example';
import './JsonInput.css';

export default function JsonInput({
  schema,         // JSON Schema (workflow.graph.stateSchema). Optional.
  onChange,       // (parsedValue, { ok, error }) => void
  disabled = false,
  initialMode = 'full', // 'full' (every field) or 'required' (minimal)
}) {
  const [text, setText] = useState(() => prettyJson(schemaToExample(schema, { mode: initialMode })));
  const [parseError, setParseError] = useState(null);
  const [schemaCollapsed, setSchemaCollapsed] = useState(true);
  const taRef = useRef(null);

  // Re-seed when the schema arrives async (e.g. modal opened before the
  // workflow row finished loading). We only overwrite if the user hasn't
  // typed yet — heuristic: text matches the previous example.
  const seededRef = useRef(text);
  useEffect(() => {
    const fresh = prettyJson(schemaToExample(schema, { mode: initialMode }));
    if (text === seededRef.current) {
      setText(fresh);
      seededRef.current = fresh;
      validateAndReport(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  const validateAndReport = useCallback((nextText) => {
    if (!nextText.trim()) {
      setParseError(null);
      onChange?.({}, { ok: true, error: null });
      return;
    }
    try {
      const parsed = JSON.parse(nextText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        const err = 'Input must be a JSON object (e.g. { "key": "value" }).';
        setParseError(err);
        onChange?.(null, { ok: false, error: err });
        return;
      }
      setParseError(null);
      onChange?.(parsed, { ok: true, error: null });
    } catch (e) {
      const err = e.message.replace(/^JSON\.parse: /, '');
      setParseError(err);
      onChange?.(null, { ok: false, error: err });
    }
  }, [onChange]);

  // Initial report so parent's `ok` reflects the seeded state.
  useEffect(() => { validateAndReport(text); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const next = e.target.value;
    setText(next);
    validateAndReport(next);
  };

  const handleReset = () => {
    const fresh = prettyJson(schemaToExample(schema, { mode: initialMode }));
    setText(fresh);
    seededRef.current = fresh;
    validateAndReport(fresh);
    taRef.current?.focus();
  };

  const handleMinimal = () => {
    const min = prettyJson(schemaToExample(schema, { mode: 'required' }));
    setText(min);
    seededRef.current = min;
    validateAndReport(min);
    taRef.current?.focus();
  };

  // Tab key inserts 2 spaces instead of moving focus.
  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const ta = taRef.current;
      if (!ta) return;
      const { selectionStart: start, selectionEnd: end } = ta;
      const next = `${text.slice(0, start)}  ${text.slice(end)}`;
      setText(next);
      validateAndReport(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 2;
      });
    }
  };

  const hasSchemaProps = schema && schema.properties && Object.keys(schema.properties).length > 0;

  return (
    <div className="json-input">
      <div className="json-input-toolbar">
        <span className="json-input-toolbar-label">Trigger payload (JSON)</span>
        <div className="json-input-toolbar-actions">
          {hasSchemaProps && (
            <>
              <button
                type="button"
                className="json-input-toolbar-btn"
                onClick={handleMinimal}
                disabled={disabled}
                title="Show only required fields"
              >
                Minimal
              </button>
              <button
                type="button"
                className="json-input-toolbar-btn"
                onClick={handleReset}
                disabled={disabled}
                title="Reset to full example from schema"
              >
                Full example
              </button>
            </>
          )}
        </div>
      </div>

      <textarea
        ref={taRef}
        className={`json-input-textarea ${parseError ? 'has-error' : ''}`}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        rows={Math.min(20, Math.max(6, text.split('\n').length + 1))}
        placeholder='{ "key": "value" }'
        aria-invalid={Boolean(parseError)}
      />

      {parseError && (
        <p className="json-input-error">⚠ {parseError}</p>
      )}

      {hasSchemaProps && (
        <details
          className="json-input-schema-details"
          open={!schemaCollapsed}
          onToggle={(e) => setSchemaCollapsed(!e.target.open)}
        >
          <summary className="json-input-schema-summary">
            Schema reference <span className="json-input-schema-count">({Object.keys(schema.properties).length} fields)</span>
          </summary>
          <pre className="json-input-schema-pre">{prettyJson(schema)}</pre>
        </details>
      )}

      {!schema && (
        <p className="json-input-hint">
          No schema available for this workflow — the payload is whatever your
          nodes expect. Submit <code>{'{}'}</code> if no input is required.
        </p>
      )}
    </div>
  );
}
